import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Activity } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    clinic_name: "", physio_name: "", city: "",
    email: "", phone: "", clinic_start_date: "", password: "",
  });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await register(form);
      toast.success("Welcome to PatientFlow");
      nav("/dashboard");
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFDFD] flex items-center justify-center p-6">
      <div className="w-full max-w-2xl">
        <div className="flex items-center gap-2 mb-6">
          <Activity className="h-6 w-6 text-emerald-600" strokeWidth={1.5} />
          <span className="font-heading text-lg font-semibold text-zinc-900">PatientFlow</span>
          <span className="ml-2 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">Beta</span>
        </div>
        <div className="bg-white border border-zinc-200 rounded-xl p-8 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
          <h2 className="font-heading text-2xl font-semibold text-zinc-900">Create your account</h2>
          <p className="text-sm text-zinc-500 mt-1">Set up your clinic profile in under a minute.</p>

          <form onSubmit={submit} className="mt-6 grid sm:grid-cols-2 gap-4" data-testid="register-form">
            <Field label="Clinic Name" testId="reg-clinic-name">
              <Input required value={form.clinic_name} onChange={upd("clinic_name")} data-testid="reg-clinic-name-input" />
            </Field>
            <Field label="Physiotherapist Name" testId="reg-physio-name">
              <Input required value={form.physio_name} onChange={upd("physio_name")} data-testid="reg-physio-name-input" />
            </Field>
            <Field label="City" testId="reg-city">
              <Input required value={form.city} onChange={upd("city")} data-testid="reg-city-input" />
            </Field>
            <Field label="Phone (optional)" testId="reg-phone">
              <Input value={form.phone} onChange={upd("phone")} data-testid="reg-phone-input" />
            </Field>
            <Field label="Email" testId="reg-email">
              <Input type="email" required value={form.email} onChange={upd("email")} data-testid="reg-email-input" />
            </Field>
            <Field label="Password" testId="reg-password">
              <Input type="password" required minLength={6} value={form.password} onChange={upd("password")} data-testid="reg-password-input" />
            </Field>
            <Field label="Clinic Start Date (optional)" testId="reg-start-date">
              <Input type="date" value={form.clinic_start_date} onChange={upd("clinic_start_date")} data-testid="reg-start-date-input" />
            </Field>

            {err && <div className="sm:col-span-2 text-sm text-red-600" data-testid="register-error">{err}</div>}

            <div className="sm:col-span-2 flex items-center justify-between mt-2">
              <Link to="/login" className="text-sm text-zinc-600 hover:text-emerald-700" data-testid="register-goto-login">Already have an account?</Link>
              <Button type="submit" disabled={loading} className="bg-emerald-600 hover:bg-emerald-700 text-white h-11 px-6" data-testid="register-submit-button">
                {loading ? "Creating..." : "Create account"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
